import { useTranslation } from 'next-i18next';

import { Box, Button, Flex, Text } from '@chakra-ui/react';

import { AdaptiveDrawer } from '@nocowanie/common-ui';
import { IconProps } from '@nocowanie/icons';

import { AmenitiesCategorized } from '@app/components/molecules/amenities-categorized';
import { AppModalsEnum } from '@app/enums';
import { getAmenityIcon } from '@app/helpers';
import { useDrawerState, useModalFragmentSync } from '@app/hooks';
import {
    accommodationStaticSlice,
    commonUiStateSlice,
    useAppDispatch,
    useAppSelector,
} from '@app/state';

import { AmenitiesMobileProps } from './amenities-mobile.props';

export const AmenitiesMobile = ({ internalAmenityGroupsToShow, ...rest }: AmenitiesMobileProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'amenities',
    });
    const dispatch = useAppDispatch();

    const isDrawerOpen = useAppSelector(
        commonUiStateSlice.selectors.selectModalData<boolean>(AppModalsEnum.Amenities),
    );

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.Amenities,
                modalData: true,
            }),
        );
    });

    const closeDrawer = () => {
        dispatch(commonUiStateSlice.actions.resetModalDataAction(AppModalsEnum.Amenities));
    };

    useModalFragmentSync(AppModalsEnum.Amenities, isDrawerOpen);

    const { services } = useAppSelector(accommodationStaticSlice.selectors.selectAmenitiesData);

    if (!internalAmenityGroupsToShow?.length || !services?.length) {
        return;
    }

    const iconConfig: IconProps = {
        size: 'md',
        variant: 'subtle',
        color: 'secondary.600',
    };

    return (
        <Box {...rest}>
            <Flex direction={'column'} gap={4}>
                {internalAmenityGroupsToShow.map(({ id, label, type, free }) => {
                    return (
                        <Flex key={id} gap={2}>
                            {getAmenityIcon(type, iconConfig)}
                            <Text>{free ? `${t('free')} ${label.toLowerCase()}` : label}</Text>
                        </Flex>
                    );
                })}
            </Flex>
            <Button
                onClick={openDrawer}
                w={'full'}
                colorPalette={'secondary'}
                mt={4}
                loading={isDrawerPending}
                disabled={isDrawerPending}
            >
                {t('seeAllAmenities', {
                    value: services.length,
                })}
            </Button>

            <AdaptiveDrawer
                isOpen={isDrawerOpen}
                onOpen={openDrawer}
                onClose={closeDrawer}
                header={t('amenitiesHeader')}
                display={'flex'}
                flexDirection={'column'}
                overflow={'auto'}
                p={4}
                mb={16}
                drawerProps={{
                    size: { base: 'full', md: 'lg' },
                    variant: 'withMargin',
                    placement: 'bottom',
                }}
            >
                <AmenitiesCategorized internalAmenityGroupsToShow={internalAmenityGroupsToShow} />
            </AdaptiveDrawer>
        </Box>
    );
};
